import type { Client, Assessment, PostureAnalysis, ClientSummary } from '@/lib/types';
import { safeToDate } from '@/lib/utils';

/**
 * Roster intelligence (Phase 3 foundation — client-side only).
 *
 * Pure, framework-free signal derivation so it is unit-testable without React.
 * All signals are computed from fields that actually exist on cached docs
 * (`Assessment`, `PostureAnalysis`) or on the lightweight `ClientSummary` — no
 * invented fields. The roster hook `useRosterQuery` builds its intelligence map
 * via `buildRosterIntelligenceFromSummaries`.
 */

/** Days since last assessment after which a reassessment is "due". Matches Dashboard. */
export const REASSESS_DAYS = 21;
/** Days since last assessment after which the client is considered lapsed/at-risk. */
export const AT_RISK_DAYS = 60;
/** A client added within this window is treated as "new" (first assessment expected). */
export const NEW_CLIENT_DAYS = 14;
/** Latest posture orientation confidence below this (%) flags a low-confidence scan. */
export const LOW_CONFIDENCE_PCT = 60;

export type RosterFlagId =
  | 'no-assessment'
  | 'new'
  | 'needs-reassessment'
  | 'at-risk'
  | 'low-confidence';

export type FlagTone = 'info' | 'warn' | 'danger';

export interface RosterFlag {
  id: RosterFlagId;
  label: string;
  tone: FlagTone;
}

export type NextActionKind = 'start-assessment' | 'reassess' | 'reshoot' | 'review';

export interface NextBestAction {
  kind: NextActionKind;
  label: string;
}

export interface ClientIntelligence {
  clientId: string;
  latestAssessmentAt: Date | null;
  latestPostureAt: Date | null;
  daysSinceAssessment: number | null;
  daysSincePosture: number | null;
  assessmentCount: number;
  postureCount: number;
  orientationConfidencePct: number | null;
  flags: RosterFlag[];
  /** Higher = more attention required. Drives the "Needs attention" sort. */
  attentionScore: number;
  nextBestAction: NextBestAction;
}

const MS_PER_DAY = 86_400_000;

const FLAG_WEIGHT: Record<RosterFlagId, number> = {
  'no-assessment': 90,
  'at-risk': 80,
  'needs-reassessment': 50,
  'low-confidence': 30,
  new: 20,
};

interface DatedDoc {
  date?: unknown;
  createdAt?: unknown;
}

function docDate(doc: DatedDoc): Date | null {
  return safeToDate(doc.date) ?? safeToDate(doc.createdAt);
}

function latestDoc<T extends DatedDoc>(docs: T[]): T | null {
  let best: T | null = null;
  let bestTime = -Infinity;
  for (const doc of docs) {
    const dt = docDate(doc);
    if (dt && dt.getTime() > bestTime) {
      best = doc;
      bestTime = dt.getTime();
    }
  }
  return best;
}

function daysBetween(from: Date | null, now: Date): number | null {
  if (!from) return null;
  return Math.floor((now.getTime() - from.getTime()) / MS_PER_DAY);
}

function scoreAttention(flags: RosterFlag[], daysSinceAssessment: number | null): number {
  let score = flags.reduce((sum, flag) => sum + FLAG_WEIGHT[flag.id], 0);
  // Tie-break by staleness so older-overdue clients rise within the same band.
  if (daysSinceAssessment != null) {
    score += Math.min(daysSinceAssessment, 365) * 0.1;
  }
  return Math.round(score * 10) / 10;
}

function pickNextAction(flags: RosterFlag[]): NextBestAction {
  const ids = new Set(flags.map((f) => f.id));
  if (ids.has('no-assessment') || ids.has('new')) {
    return { kind: 'start-assessment', label: 'Start assessment' };
  }
  if (ids.has('at-risk')) {
    return { kind: 'reassess', label: 'Reassess — lapsed' };
  }
  if (ids.has('needs-reassessment')) {
    return { kind: 'reassess', label: 'Reassess' };
  }
  if (ids.has('low-confidence')) {
    return { kind: 'reshoot', label: 'Re-shoot scan' };
  }
  return { kind: 'review', label: 'On track' };
}

interface IntelligenceInputs {
  assessmentCount: number;
  postureCount: number;
  latestAssessmentAt: Date | null;
  latestPostureAt: Date | null;
  orientationConfidencePct: number | null;
}

/**
 * Shared signal derivation. Both the full-array path ({@link computeClientIntelligence})
 * and the summary path ({@link buildRosterIntelligenceFromSummaries}) reduce their
 * inputs to these primitives so the flag/attention logic lives in exactly one place.
 */
function deriveClientIntelligence(
  client: Client,
  inputs: IntelligenceInputs,
  now: Date
): ClientIntelligence {
  const {
    assessmentCount,
    postureCount,
    latestAssessmentAt,
    latestPostureAt,
    orientationConfidencePct,
  } = inputs;

  const daysSinceAssessment = daysBetween(latestAssessmentAt, now);
  const daysSincePosture = daysBetween(latestPostureAt, now);

  const createdAt = safeToDate(client.createdAt);
  const ageDays = daysBetween(createdAt, now);
  const isNew = ageDays != null && ageDays <= NEW_CLIENT_DAYS;

  const flags: RosterFlag[] = [];

  if (assessmentCount === 0) {
    if (isNew) {
      flags.push({ id: 'new', label: 'New', tone: 'info' });
    } else {
      flags.push({ id: 'no-assessment', label: 'No assessment', tone: 'danger' });
    }
  } else if (daysSinceAssessment != null) {
    if (daysSinceAssessment > AT_RISK_DAYS) {
      flags.push({ id: 'at-risk', label: 'Lapsed', tone: 'danger' });
    } else if (daysSinceAssessment > REASSESS_DAYS) {
      flags.push({ id: 'needs-reassessment', label: 'Reassess due', tone: 'warn' });
    }
  }

  // Client-side, orientation-only low-confidence check (no server signal input).
  if (orientationConfidencePct != null && orientationConfidencePct < LOW_CONFIDENCE_PCT) {
    flags.push({ id: 'low-confidence', label: 'Low-confidence scan', tone: 'warn' });
  }

  return {
    clientId: client.id,
    latestAssessmentAt,
    latestPostureAt,
    daysSinceAssessment,
    daysSincePosture,
    assessmentCount,
    postureCount,
    orientationConfidencePct,
    flags,
    attentionScore: scoreAttention(flags, daysSinceAssessment),
    nextBestAction: pickNextAction(flags),
  };
}

/** Derive clinical signals for a single client from its assessments + posture analyses. */
export function computeClientIntelligence(
  client: Client,
  assessments: Assessment[],
  postureAnalyses: PostureAnalysis[],
  now: Date = new Date()
): ClientIntelligence {
  const latestAssessment = latestDoc(assessments);
  const latestPosture = latestDoc(postureAnalyses);

  return deriveClientIntelligence(
    client,
    {
      assessmentCount: assessments.length,
      postureCount: postureAnalyses.length,
      latestAssessmentAt: latestAssessment ? docDate(latestAssessment) : null,
      latestPostureAt: latestPosture ? docDate(latestPosture) : null,
      orientationConfidencePct: latestPosture?.classification?.orientationConfidencePct ?? null,
    },
    now
  );
}

function groupByClientId<T extends { clientId: string }>(docs: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const doc of docs ?? []) {
    const list = map.get(doc.clientId);
    if (list) list.push(doc);
    else map.set(doc.clientId, [doc]);
  }
  return map;
}

/** Build the per-client intelligence map from flat cache arrays. */
export function buildRosterIntelligence(
  clients: Client[],
  allAssessments: Array<Assessment & { clientId: string }>,
  allPostureAnalyses: Array<PostureAnalysis & { clientId: string }>,
  now: Date = new Date()
): Map<string, ClientIntelligence> {
  const assessmentsByClient = groupByClientId(allAssessments ?? []);
  const postureByClient = groupByClientId(allPostureAnalyses ?? []);

  const map = new Map<string, ClientIntelligence>();
  for (const client of clients) {
    map.set(
      client.id,
      computeClientIntelligence(
        client,
        assessmentsByClient.get(client.id) ?? [],
        postureByClient.get(client.id) ?? [],
        now
      )
    );
  }
  return map;
}

/**
 * Build the per-client intelligence map from the lightweight client summaries
 * (counts + latest timestamps) exposed by the route-scoped clients cache. This is
 * the roster's source of truth now that the cache no longer eagerly loads every
 * client's assessments/posture analyses — the counts drive the "No assessment",
 * "Reassess due", and "Lapsed" flags without a full detail fetch per client.
 */
export function buildRosterIntelligenceFromSummaries(
  clients: Client[],
  summaries: ClientSummary[],
  now: Date = new Date()
): Map<string, ClientIntelligence> {
  const summaryByClient = new Map<string, ClientSummary>();
  for (const summary of summaries ?? []) {
    summaryByClient.set(summary.clientId, summary);
  }

  const map = new Map<string, ClientIntelligence>();
  for (const client of clients) {
    const summary = summaryByClient.get(client.id);
    map.set(
      client.id,
      deriveClientIntelligence(
        client,
        {
          assessmentCount: summary?.assessmentCount ?? 0,
          postureCount: summary?.analysisCount ?? 0,
          latestAssessmentAt: safeToDate(summary?.latestAssessmentAt),
          latestPostureAt: safeToDate(summary?.latestAnalysisAt),
          orientationConfidencePct: null,
        },
        now
      )
    );
  }
  return map;
}
